// ✅ src/components/TopBar.js (FULL)
// ✅ FIX: Bell badge now shows unread COUNT (1,2,3...)
// ✅ FIX: TopBar listens directly to Firestore notifications/{uid}/items (read == false)
// ✅ Works even if you never open NotificationsScreen

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}
function badgeText(n) {
  const x = Number(n) || 0;
  if (x <= 0) return "";
  if (x > 99) return "99+";
  return String(x);
}

/* ---------------- UI bits ---------------- */
function TopIconBtn({ label, icon, onClick, badgeCount }) {
  const show = Number(badgeCount) > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      style={S.topIconBtn}
      aria-label={label}
      title={label}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>

      {show ? (
        <span style={S.badgeNum} aria-label={`${badgeCount} unread`}>
          {badgeText(badgeCount)}
        </span>
      ) : null}
    </button>
  );
}

/* ---------------- main ---------------- */
export default function TopBar() {
  const nav = useNavigate();
  const location = useLocation();

  const [me, setMe] = useState({ displayName: "Me", avatar: "" });
  const [uid, setUid] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);

  const title = useMemo(() => {
    const p = location.pathname || "/";
    if (p === "/profile") return "Home";
    if (p.startsWith("/home")) return "Home";
    if (p.startsWith("/likes")) return "Likes";
    if (p.startsWith("/matches")) return "Matches";
    if (p.startsWith("/settings")) return "Settings";
    if (p.startsWith("/leaderboard")) return "Leaderboard";
    if (p.startsWith("/profile-details")) return "My Profile";
    if (p.startsWith("/edit-profile")) return "Edit Profile";
    if (p.startsWith("/chats") || p.startsWith("/chat/")) return "Messages";
    if (p.startsWith("/meetups")) return "Meetups";
    if (p.startsWith("/upgrade")) return "Upgrade";
    return "Leaderboard";
  }, [location.pathname]);

  // ✅ load profile + set uid
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUid("");
        setUnreadCount(0);
        return;
      }

      setUid(u.uid);

      try {
        const snap = await getDoc(doc(db, "datingProfiles", u.uid));
        const data = snap.exists() ? snap.data() : {};
        setMe({
          displayName: safeStr(data?.displayName) || "Me",
          avatar: safeStr(data?.avatar) || safeStr(data?.photos?.[0]),
        });
      } catch {
        setMe({ displayName: "Me", avatar: "" });
      }
    });

    return () => unsub();
  }, []);

  // ✅ REALTIME unread badge listener (works globally)
  useEffect(() => {
    if (!uid) return;

    // listen only unread docs
    const qy = query(
      collection(db, "notifications", uid, "items"),
      where("read", "==", false),
      limit(200)
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const n = snap.size || 0;
        setUnreadCount(n);
      },
      () => {
        // if rules/network fail, just hide badge instead of crashing
        setUnreadCount(0);
      }
    );

    return () => unsub();
  }, [uid]);

  return (
    <div style={S.topBar}>
      <div style={S.brand}>
        <div style={S.brandDot} />
        <div style={{ minWidth: 0 }}>
          <div style={S.topTitle}>{title}</div>
          <div style={S.topSub}> </div>
        </div>
      </div>

      <div style={S.topActions}>
        <TopIconBtn label="Leaderboard" icon="🏆" onClick={() => nav("/leaderboard")} />

        <TopIconBtn
          label="Notifications"
          icon="🔔"
          onClick={() => nav("/notifications")}
          badgeCount={unreadCount}
        />

        <button
          type="button"
          style={S.upgradeBtn}
          onClick={() => nav("/upgrade")}
          aria-label="Upgrade"
          title="Upgrade"
        >
          ⭐ Upgrade
        </button>

        <button
          type="button"
          style={S.profileBtn}
          onClick={() => nav("/profile-details")}
          aria-label="Profile"
          title="Profile"
        >
          {safeStr(me?.avatar) ? (
            <img src={safeStr(me.avatar)} alt="me" style={S.profileImg} />
          ) : (
            <div style={S.profileFallback}>{initials(me?.displayName || "Me")}</div>
          )}
        </button>
      </div>
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  topBar: {
    width: "min(980px, calc(100% - 24px))",
    margin: "0 auto",
    padding: 14,
    borderRadius: 22,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 18px 50px rgba(2,6,23,0.08)",
    backdropFilter: "blur(12px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  brand: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  brandDot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))",
    boxShadow: "0 0 0 6px rgba(236,72,153,.10)",
    flexShrink: 0,
  },

  topTitle: {
    fontWeight: 1000,
    letterSpacing: -0.4,
    fontSize: 16,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  topSub: { fontWeight: 800, opacity: 0.65, fontSize: 12, minHeight: 14 },

  topActions: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0 },

  topIconBtn: {
    position: "relative",
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.75)",
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(2,6,23,0.06)",
  },

  // ✅ number badge (1,2,3...)
  badgeNum: {
    position: "absolute",
    top: 6,
    right: 6,
    minWidth: 18,
    height: 18,
    padding: "0 6px",
    borderRadius: 999,
    background: "#ec4899",
    color: "#fff",
    fontWeight: 1000,
    fontSize: 11,
    display: "grid",
    placeItems: "center",
    lineHeight: "18px",
    boxShadow: "0 0 0 4px rgba(236,72,153,.18)",
  },

  upgradeBtn: {
    height: 44,
    padding: "0 14px",
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.75)",
    cursor: "pointer",
    fontWeight: 1000,
    boxShadow: "0 10px 24px rgba(2,6,23,0.06)",
    whiteSpace: "nowrap",
  },

  profileBtn: {
    width: 44,
    height: 44,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.75)",
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(2,6,23,0.06)",
    overflow: "hidden",
    padding: 0,
  },
  profileImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  profileFallback: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    background: "rgba(2,6,23,0.75)",
    color: "#fff",
    fontWeight: 1000,
  },
};